import * as Sentry from '@sentry/react';

// Type definitions for better type safety
export interface ErrorCategory {
  issue_category:
    'api_error' | 'third_party' | 'runtime_error' | 'user_interaction';
  issue_type: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
  group: string;
  confidence: 'high' | 'medium' | 'low'; // How confident we are in the categorization
}

// Configuration for third-party services
const THIRD_PARTY_SERVICES = [
  {
    name: 'stripe',
    priority: 'high' as const,
    keywords: ['stripe', 'card', 'payment']
  },
  {
    name: 'clarity',
    priority: 'low' as const,
    keywords: ['clarity', 'microsoft']
  },
  { name: 'sentry', priority: 'low' as const, keywords: ['sentry', '@sentry'] },
  {
    name: 'firebase',
    priority: 'medium' as const,
    keywords: ['firebase', 'google']
  }
];

// HTTP status code patterns
const HTTP_STATUS_PATTERNS = {
  authentication: /\b(401|403)\b/,
  client_error: /\b(4[0-9][0-9])\b/,
  server_error: /\b(5[0-9][0-9])\b/,
  not_found: /\b404\b/
};

// Runtime error patterns
const RUNTIME_ERROR_PATTERNS = [
  {
    type: 'null_undefined',
    pattern:
      /cannot read propert(y|ies)|undefined|null is not|cannot access before initialization/i,
    priority: 'medium'
  },
  {
    type: 'reference_error',
    pattern: /is not defined|not a function|is not a constructor/i,
    priority: 'medium'
  },
  {
    type: 'type_error',
    pattern: /cannot convert|invalid|not a valid/i,
    priority: 'medium'
  },
  {
    type: 'syntax_error',
    pattern: /unexpected token|invalid syntax|missing/i,
    priority: 'high'
  },
  {
    type: 'network_error',
    pattern: /network error|fetch.*failed|timeout|connection|cors/i,
    priority: 'high'
  }
];

// Helper functions for better organization and testability

function isUserInteractionError(
  event: Sentry.ErrorEvent,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tags: Record<string, any>
): boolean {
  return (
    event.type === 'transaction' &&
    (tags.transaction || tags['replay.click_selector']) &&
    (tags['replay.rage_click'] || tags.mechanism?.type === 'rage_click')
  );
}

function categorizeApiError(
  errorMessage: string,
  url: string
  // event: Sentry.ErrorEvent
): ErrorCategory | null {
  // Check for HTTP status codes first (most reliable)
  if (HTTP_STATUS_PATTERNS.authentication.test(errorMessage)) {
    return {
      issue_category: 'api_error',
      issue_type: 'authentication',
      priority: 'high',
      group: 'security',
      confidence: 'high'
    };
  }

  if (HTTP_STATUS_PATTERNS.server_error.test(errorMessage)) {
    return {
      issue_category: 'api_error',
      issue_type: 'server_error',
      priority: 'high',
      group: 'backend',
      confidence: 'high'
    };
  }

  if (HTTP_STATUS_PATTERNS.not_found.test(errorMessage)) {
    return {
      issue_category: 'api_error',
      issue_type: 'not_found',
      priority: 'low',
      group: 'routing',
      confidence: 'high'
    };
  }

  if (HTTP_STATUS_PATTERNS.client_error.test(errorMessage)) {
    return {
      issue_category: 'api_error',
      issue_type: 'client_error',
      priority: 'medium',
      group: 'frontend',
      confidence: 'high'
    };
  }

  // Network-related errors
  const networkErrorPattern = RUNTIME_ERROR_PATTERNS.find(
    (entry) => entry.type === 'network_error'
  )?.pattern;
  if (networkErrorPattern && networkErrorPattern.test(errorMessage)) {
    const isTimeout = /timeout/i.test(errorMessage);
    return {
      issue_category: 'api_error',
      issue_type: isTimeout ? 'timeout' : 'network_error',
      priority: isTimeout ? 'medium' : 'high',
      group: 'connectivity',
      confidence: 'high'
    };
  }

  // Check if URL suggests it's an API call
  if (url.includes('/api/') || errorMessage.includes('fetch')) {
    return {
      issue_category: 'api_error',
      issue_type: 'unknown_api_error',
      priority: 'medium',
      group: 'backend',
      confidence: 'medium'
    };
  }

  return null;
}

function categorizeThirdPartyError(
  errorMessage: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  stack: any[],
  error?: Error
): ErrorCategory | null {
  // Check stack trace for third-party libraries
  const stackString = error?.stack?.toLowerCase() || '';
  const hasNodeModules =
    stackString.includes('node_modules') ||
    stack.some((frame) => frame.filename?.includes('node_modules'));

  // Check for specific third-party services
  const matchedService = THIRD_PARTY_SERVICES.find((service) => {
    return service.keywords.some(
      (keyword) =>
        errorMessage.includes(keyword) ||
        stackString.includes(keyword) ||
        stack.some((frame) => frame.filename?.includes(keyword))
    );
  });

  if (matchedService) {
    return {
      issue_category: 'third_party',
      issue_type: matchedService.name,
      priority: matchedService.priority,
      group: 'dependencies',
      confidence: 'high'
    };
  }

  // Generic third-party library error
  if (hasNodeModules) {
    return {
      issue_category: 'third_party',
      issue_type: 'external_library',
      priority: 'medium',
      group: 'dependencies',
      confidence: 'medium'
    };
  }

  return null;
}

function categorizeRuntimeError(
  errorMessage: string,
  errorType: string
): ErrorCategory | null {
  // Check for specific runtime error patterns
  const matchedPattern = (
    RUNTIME_ERROR_PATTERNS as readonly {
      type: string;
      pattern: RegExp;
      priority: ErrorCategory['priority'];
    }[]
  ).find(
    (entry) => entry.pattern.test(errorMessage) || entry.pattern.test(errorType)
  );

  if (matchedPattern) {
    let priority: ErrorCategory['priority'];
    if (matchedPattern.type === 'null_undefined') {
      priority = 'medium';
    } else if (matchedPattern.type === 'network_error') {
      priority = 'high';
    } else {
      priority = 'medium';
    }

    return {
      issue_category: 'runtime_error',
      issue_type: matchedPattern.type,
      priority,
      group: 'code_quality',
      confidence: 'high'
    };
  }

  // Check by error type
  const typeMapping: Record<
    string,
    { type: string; priority: ErrorCategory['priority'] }
  > = {
    TypeError: { type: 'type_error', priority: 'medium' },
    ReferenceError: { type: 'reference_error', priority: 'medium' },
    SyntaxError: { type: 'syntax_error', priority: 'high' },
    RangeError: { type: 'range_error', priority: 'medium' },
    URIError: { type: 'uri_error', priority: 'low' }
  };

  const mapping = typeMapping[errorType];
  if (mapping) {
    return {
      issue_category: 'runtime_error',
      issue_type: mapping.type,
      priority: mapping.priority,
      group: 'code_quality',
      confidence: 'high'
    };
  }

  return null;
}

// Utility function to get human-readable category description
export const getCategoryDescription = (category: ErrorCategory): string => {
  const descriptions: Record<string, string> = {
    api_error: 'API/Network Issues',
    third_party: 'Third-Party Service Issues',
    runtime_error: 'JavaScript Runtime Errors',
    user_interaction: 'User Experience Issues',
    performance: 'Performance Issues'
  };

  return descriptions[category.issue_category] || 'Unknown Issue Type';
};

export const categorizeError = (
  event: Sentry.ErrorEvent,
  hint: Sentry.EventHint
): ErrorCategory => {
  const error = hint.originalException as Error | undefined;
  const sentryException = event.exception?.values?.[0];
  const errorMessage = (
    error?.message ||
    sentryException?.value ||
    event.message ||
    ''
  ).toLowerCase();
  const errorType = sentryException?.type || error?.name || '';
  const stack = sentryException?.stacktrace?.frames || [];
  const tags = event.tags || {};
  const url = event.request?.url || event.contexts?.trace?.data?.url || '';

  // 1. User Interaction Errors (Rage clicks, etc.)
  if (isUserInteractionError(event, tags)) {
    return {
      issue_category: 'user_interaction',
      issue_type: 'rage_click',
      priority: 'low',
      group: 'user_experience',
      confidence: 'high'
    };
  }

  // Explicit API Error Tag Check
  if (tags.error_type === 'api_error') {
    return {
      issue_category: 'api_error',
      issue_type: 'explicit_api_error',
      priority: 'high',
      group: 'backend',
      confidence: 'high'
    };
  }

  // 2. API/Network Errors (most reliable detection first)
  const apiError = categorizeApiError(errorMessage, url);
  if (apiError) return apiError;

  // 3. Third-party Library Errors
  const thirdPartyError = categorizeThirdPartyError(errorMessage, stack, error);
  if (thirdPartyError) return thirdPartyError;

  // 4. Runtime Errors
  const runtimeError = categorizeRuntimeError(errorMessage, errorType);
  if (runtimeError) return runtimeError;

  // Default fallback
  return {
    issue_category: 'runtime_error',
    issue_type: 'unknown',
    priority: 'medium',
    group: 'general',
    confidence: 'low'
  };
};

const EXPECTED_API_STATUS = /\b(401|404)\b/;
const AUTH_ENDPOINT = /\/(login|token|one-time-token|refresh)\b/;

/**
 * True for the API errors we sample instead of keeping in full: 401/404 on
 * anything other than an auth endpoint (token expiry, app-bridge probes). Auth
 * endpoints are always kept so a real login outage stays visible.
 */
export const isExpectedApiError = (
  category: ErrorCategory,
  event: Sentry.Event,
  hint: Sentry.EventHint
): boolean => {
  if (category.issue_category !== 'api_error') return false;
  const original = hint.originalException as
    { message?: string; config?: { url?: string } } | undefined;
  const message =
    event.exception?.values?.[0]?.value || original?.message || event.message;
  const url = event.request?.url || original?.config?.url || '';
  return EXPECTED_API_STATUS.test(message || '') && !AUTH_ENDPOINT.test(url);
};

/**
 * Replay `beforeErrorSampling` hook: skip the buffered-replay upload only for
 * expected API outcomes, so 5xx errors keep their replay. Runs after
 * `beforeSend` and receives no hint, so classify from the event alone.
 */
export const shouldReplayError = (event: Sentry.ErrorEvent): boolean =>
  !isExpectedApiError(categorizeError(event, {}), event, {});
